import React, { lazy, useCallback, useContext, useMemo, useState } from 'react'
import { ChildContainerProps } from '../../../types/types'
import { LazyComponent, Menu, RdmsMenuContextProps } from '../../../types/menu'
import { CommonContext } from '../../common/context/commonContext'
import { useLocation } from 'react-router'
import { matchPath, useNavigate } from 'react-router-dom'
import { To } from '@remix-run/router'
import { NavigateOptions } from 'react-router/dist/lib/context'
import {useTranslation} from "react-i18next";
import { tokens } from 'locales/tokens';

export const RdmsMenuContext = React.createContext({} as RdmsMenuContextProps)

export const RdmsMenuContextProvider = (props: ChildContainerProps) => {
    const { $http, $toast } = useContext(CommonContext)
    const { t, i18n } = useTranslation();
    const location = useLocation()
    const navigate = useNavigate()
    const [menus, setMenus] = useState<Menu[]>([])
    const [lazyComponents, setLazyComponents] = useState<LazyComponent[]>([])
    const [currentHistory, setCurrentHistory] = useState<any>()

    const $markHistory = useCallback(
        (initProps: any) => {
            return currentHistory || initProps
        },
        [currentHistory],
    )

    const findSidebarMenus = useCallback(() => {
        const makeLazyComponents = (menus: Menu[]) => {
            const makeLazyMenus: LazyComponent[] = []

            for (const menu of menus) {
                let component

                if (menu.path) {
                    const replacePath = menu.path.replaceAll(/\/:[a-zA-Z]+/gi, '')
                    let language = i18n.resolvedLanguage;

                    component = lazy(() => import(`../../../pages${replacePath}.tsx`))
                    makeLazyMenus.push({
                        to: menu.to,
                        componentName: language === 'ko' ? menu.label : menu.japanLabel,
                        component,
                    })
                }

                if (menu.items) {
                    makeLazyMenus.push(...makeLazyComponents(menu.items))
                }
            }

            return makeLazyMenus
        }

        $http()
            .get('/auth/my-menu')
            .then((result: Menu[]) => {
                if (!result) {
                    return
                }

                setMenus(result)
                setLazyComponents(makeLazyComponents(result))
            })
    }, [$http])

    const findParentMenu = useCallback((menus: Menu[], parentMenuId: number): Menu | undefined => {
        for (const menu of menus) {
            if (menu.key === parentMenuId) {
                return menu
            }

            if (menu.items) {
                const parentMenu: Menu | undefined = findParentMenu(menu.items, parentMenuId)

                if (parentMenu) {
                    return parentMenu
                }
            }
        }
    }, [])

    const getCurrentMenu = useCallback(() => {
        const findMenu = (menus: Menu[], parentMenu?: Menu): Menu | undefined => {
            for (const menu of menus) {
                if (menu.to && matchPath({ path: menu.to }, location.pathname)) {
                    return menu
                }

                if (menu.items) {
                    const childMenu: Menu | undefined = findMenu(menu.items, menu)

                    if (childMenu) {
                        childMenu.parentMenu = findParentMenu(menus, Number(childMenu.parentMenuId))
                        return childMenu
                    }
                }
            }

            return undefined
        }

        if (menus && location) {
            return findMenu(menus)
        }
    }, [location, menus, findParentMenu])

    const getGotoMenu = useCallback(
        (to: To) => {
            let path = typeof to === 'string' ? to : to.pathname

            if (path) {
                const findMenu = (menus: Menu[]): Menu | undefined => {
                    for (const menu of menus) {
                        if (menu.to) {
                            if (menu.to && matchPath({ path: menu.to }, path || '')) {
                                return menu
                            }
                        }

                        if (menu.items) {
                            const childMenu: Menu | undefined = findMenu(menu.items)

                            if (childMenu) {
                                childMenu.parentMenu = findParentMenu(menus, Number(childMenu.parentMenuId))
                                return childMenu
                            }
                        }
                    }

                    return undefined
                }

                return findMenu(menus)
            }

            return undefined
        },
        [menus, findParentMenu],
    )

    const clearCurrentHistory = useCallback(() => {
        setCurrentHistory(undefined)
    }, [])

    // useEffect(() => {
    //     clearCurrentHistory()
    // }, [navigate, clearCurrentHistory])

    const $navigate = useCallback(
        (to: To | number, option?: NavigateOptions, params?: any, ignore: boolean = false) => {
            const jsonHistory = sessionStorage.getItem('history')
            let history = jsonHistory ? JSON.parse(jsonHistory) : []
            const currentMenu = getCurrentMenu()

            if (typeof to === 'number') {
                const markHistory = history[history.length + to]

                if (markHistory) {
                    setCurrentHistory(markHistory.params)
                    $navigate(markHistory.to, markHistory.option, markHistory.params, true)
                } else {
                    navigate(to)
                }
            } else {
                // const beforeHistory = history[history.length - 1]
                // if (!ignore && beforeHistory && beforeHistory.to === to) {
                //     return
                // }

                const goToMenu = getGotoMenu(to)

                if (goToMenu && goToMenu.menuTypeCd === 'CHILD_APP') {
                    if (currentMenu && currentMenu.menuTypeCd === 'APP' && !currentMenu.subAcesYn) {
                        $toast({
                            title: t(tokens.common.failure),
                            text: t(tokens.common.no_access_rights),
                            icon: 'warn',
                        })

                        return
                    }

                    if (goToMenu.parentMenu && !goToMenu.parentMenu.subAcesYn) {
                        $toast({
                            title: t(tokens.common.failure),
                            text: t(tokens.common.no_access_rights),
                            icon: 'warn',
                        })

                        return
                    }
                }

                if (history.length >= 50) {
                    history = [...history.splice(50 - history.length, history.length)]
                }

                if (currentMenu) {
                    history.push({ to: currentMenu.to, option, params })
                    setCurrentHistory(params)
                }
                sessionStorage.setItem('history', JSON.stringify(history))
                navigate(to, option)
            }
        },
        [navigate, getCurrentMenu, getGotoMenu, $toast],
    )

    const value = useMemo(
        () => ({
            menus,
            lazyComponents,
            getCurrentMenu,
            findSidebarMenus,
            $markHistory,
            $navigate,
            clearCurrentHistory,
        }),
        [menus, getCurrentMenu, lazyComponents, findSidebarMenus, $markHistory, $navigate, clearCurrentHistory],
    )

    return <RdmsMenuContext.Provider value={value}>{props.children}</RdmsMenuContext.Provider>
}
